import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight } from 'lucide-react';
import { FONT_SANS } from '../constants';
import { colors } from '../theme/colors';
import { useLanguage } from '../context/LanguageContext';

const Section = styled.section`
  padding: clamp(64px, 9vh, 104px) clamp(20px, 5vw, 48px);
  background: ${colors.bg};
`;

const Inner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
`;

const Eyebrow = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  font-family: ${FONT_SANS};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.accent};
  background: ${colors.accentSoft};
  border: 1px solid rgba(15, 118, 110, 0.18);
`;

const Title = styled.h2`
  font-family: ${FONT_SANS};
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 800;
  color: ${colors.ink};
  margin: 18px 0 16px;
`;

const Body = styled.p`
  font-family: ${FONT_SANS};
  font-size: clamp(1rem, 1.8vw, 1.125rem);
  line-height: 1.7;
  color: ${colors.inkSoft};
  margin: 0 auto 28px;
  max-width: 680px;
`;

const Bullets = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 auto 32px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  max-width: 620px;
  text-align: left;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Bullet = styled.li`
  font-family: ${FONT_SANS};
  font-size: 15px;
  line-height: 1.5;
  color: ${colors.ink};
  display: flex;
  align-items: flex-start;
  gap: 10px;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    margin-top: 8px;
    border-radius: 50%;
    background: ${colors.accent};
  }
`;

const Cta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${FONT_SANS};
  font-size: 15px;
  font-weight: 700;
  color: ${colors.primaryText};
  background: ${colors.primary};
  text-decoration: none;
  padding: 12px 22px;
  border-radius: 999px;
  transition: background 0.15s;

  &:hover {
    background: ${colors.primaryHover};
  }
`;

function AppIntro() {
  const { t } = useLanguage();
  const intro = t.homeIntro;

  return (
    <Section id="about-omw" aria-label={intro.title}>
      <Inner>
        <Eyebrow>{intro.eyebrow}</Eyebrow>
        <Title>{intro.title}</Title>
        <Body>{intro.body}</Body>
        <Bullets>
          {intro.bullets.map((item, i) => (
            <Bullet key={i}>{item}</Bullet>
          ))}
        </Bullets>
        <Cta to="/about">
          {intro.cta}
          <ArrowRight size={18} />
        </Cta>
      </Inner>
    </Section>
  );
}

export default AppIntro;
